import {
  getInsuranceProducts,
  getInsuranceProductsBySubscription, getInsuranceUpdateQuote, sendOptOutRequest, sendPassportCardOptOutRequest,
  updateInsurance,
  getInsuranceQuote,
  getPassportCardCustomerDetails,
  updatePassportCardCustomerDetails as updatePassportCardCustomerDetailsApi,
} from 'api/insurance'
import {
  getInsuranceItems,
  getNoProtectionPassportCardInsuranceItem,
  getNoProtectionXCoverInsuranceItems,
  getSelectedPassportCardInsuranceItem,
} from 'checkout/selectors/view/insurance'
import getCheckoutInsuranceQuotes from 'checkout/selectors/view/getCheckoutInsuranceQuotes'
import getCheckoutInsuranceProducts from 'checkout/selectors/view/getCheckoutInsuranceProducts'
import getPassportCardCheckoutQuoteRequestParams from 'checkout/selectors/request/getPassportCardCheckoutQuoteRequestParams'
import { providerIsCoverGenius } from 'lib/insurance/insuranceHelpers'
import { getPassportCardQuoteRequestKey } from 'lib/insurance/passportCardQuoteUtils'
import { API_CALL, RESET_INSURANCE, INVALIDATE_INSURANCE_QUOTES } from './actionConstants'
import {
  FETCH_INSURANCE_PRODUCTS, FETCH_INSURANCE_QUOTE,
  FETCH_INSURANCE_UPDATE_QUOTE,
  FETCH_PASSPORT_CARD_CUSTOMER_DETAILS,
  OPT_OUT_OF_QUOTE,
  UPDATE_INSURANCE,
} from './apiActionConstants'
import type { AppAction } from './types/ActionTypes'
import { isInMobileApp } from 'lib/web/deviceUtils'
import { getOrderInsuranceUpdateQuoteKey } from 'lib/order/orderInsuranceUtils'
import { getFullOrder } from 'api/order'
import { updateOrder } from './OrderActions'
import { getInsuranceProductListKey, getInsuranceQuoteKey, getLuxVerticals, insuranceTypeToInsuranceProductType } from 'lib/insurance/insuranceUtils'
import { pushWithRegion } from './NavigationActions'
import { selectShouldUseInsuranceMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'

export interface FetchQuoteParams {
  destinationCountries: Array<string>
  coverAmount: number
  startDate: string
  endDate: string
  travellers: App.Occupants | Array<App.Occupants>
  currency?: string
  cartId?: string
  isCruise?: boolean
  numberOfSeniors?: number
  type: App.InsuranceProductType
  /**
   * List of policy ids - used to fetch parts of a custom quote
  */
  customPolicyIds?: Array<string>
  /**
   * Instructs svc-insurance to cache the quote and to use cached values when available
  */
  useCache?: boolean
}

export function fetchInsuranceQuote(
  productId: string,
  params: FetchQuoteParams,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const key = getInsuranceQuoteKey(productId, params)

    if (
      state.insurance.quotes[key] ||
      state.insurance.fetchingQuotes[key] ||
      state.insurance.fetchQuotesErrors[key]
    ) {
      // already have this quote or are already getting it
      return
    }

    const isLoyaltyEnabled = getIsLoyaltyEnabled(state)

    dispatch({
      type: API_CALL,
      api: FETCH_INSURANCE_QUOTE,
      key,
      request: () => getInsuranceQuote(
        productId,
        selectShouldUseInsuranceMemberPrice(state),
        {
          currency: params.currency ?? state.geo.currentCurrency,
          regionCode: state.geo.currentRegionCode,
          coverAmount: params.coverAmount,
          destinationCountries: params.destinationCountries,
          startDate: params.startDate,
          endDate: params.endDate,
          travellers: params.travellers,
          isMobileApp: isInMobileApp(state.system.rawUserAgentString),
          spoofingAgentId: state.auth.account.agentId,
          cartId: params.cartId,
          isCruise: params.isCruise,
          numberOfSeniors: params.numberOfSeniors,
          customPolicyIds: params.customPolicyIds,
          type: params.type,
          key,
          luxVerticals: getLuxVerticals(state.checkout.cart.items),
          loyaltyTier: isLoyaltyEnabled ? state.loyalty.account.data?.tier : undefined,
          useCache: params.useCache,
        }),
    })
  }
}

interface PassportCardOptOutData {
  quoteId: string
  proposalId: string
}

function getPassportCardOptOutQuoteFromCheckout(state: App.State): App.PassportCardQuote | undefined {
  const requestParams = getPassportCardCheckoutQuoteRequestParams(state)

  if (!requestParams) {
    return undefined
  }

  return state.passportCardInsurance.quotes[getPassportCardQuoteRequestKey(requestParams)]
}

function getPassportCardOptOutDataFromCheckout(state: App.State): PassportCardOptOutData | undefined {
  const noProtectionPassportCardInsuranceItem = getNoProtectionPassportCardInsuranceItem(state)

  if (
    noProtectionPassportCardInsuranceItem?.quoteId &&
    noProtectionPassportCardInsuranceItem.passportCardProposalId
  ) {
    return {
      quoteId: noProtectionPassportCardInsuranceItem.quoteId,
      proposalId: noProtectionPassportCardInsuranceItem.passportCardProposalId,
    }
  }

  // Older PassportCard no-protection items did not carry quote/proposal ids, so fall back to the
  // keyed quote lookup to avoid opting out a stale cached proposal when checkout inputs changed.
  const quote = getPassportCardOptOutQuoteFromCheckout(state)
  const proposal = quote?.proposals[0]

  if (!quote || !proposal) {
    return undefined
  }

  return {
    quoteId: quote.quoteId,
    proposalId: proposal.proposalId,
  }
}

/**
 * Sends checkout insurance opt-outs after a successful purchase.
 *
 * XCover/Cover Genius opt-out uses a no-protection checkout item and sends product_ids.
 * PassportCard opt-out uses a PassportCard no-protection checkout item to match cached quote/proposal ids
 * and sends proposal_id.
 */
export function sendCheckoutInsuranceOptOut(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const purchasedInsuranceItems = getInsuranceItems(state)
    const noProtectionXCoverInsuranceItems = getNoProtectionXCoverInsuranceItems(state)
    const noProtectionPassportCardInsuranceItem = getNoProtectionPassportCardInsuranceItem(state)
    const purchasedPassportCardInsuranceItem = getSelectedPassportCardInsuranceItem(state)

    const noProtectionXCoverInsuranceProductType = noProtectionXCoverInsuranceItems.length > 0 && noProtectionXCoverInsuranceItems[0].insuranceType ?
        insuranceTypeToInsuranceProductType(noProtectionXCoverInsuranceItems[0].insuranceType) :
      undefined

    // XCover opt-out: send only when the customer selected no protection,
    // no paid insurance item is selected, and this region/product uses Cover Genius.
    const shouldSendXCoverOptOut = (
      providerIsCoverGenius(state.geo.currentRegionCode, noProtectionXCoverInsuranceProductType) &&
      purchasedInsuranceItems.length < 1 &&
      noProtectionXCoverInsuranceItems.length > 0
    )

    if (shouldSendXCoverOptOut) {
      const xCoverInsuranceType = noProtectionXCoverInsuranceItems[0].insuranceType
      const xCoverInsuranceProductType = xCoverInsuranceType ? insuranceTypeToInsuranceProductType(xCoverInsuranceType) : undefined

      if (xCoverInsuranceProductType) {
        const xCoverQuotes = getCheckoutInsuranceQuotes(state, xCoverInsuranceProductType)
        const xCoverProducts = getCheckoutInsuranceProducts(state, xCoverInsuranceProductType)
        if (xCoverQuotes.length > 0 && xCoverProducts.length > 0) {
          // Simply take the first quote - logic taken from old implementation
          const quoteId = xCoverQuotes[0].id

          // In case where the full protection product is present, XCover requires sending the opt out with this product.
          // Otherwise, we should send the first product id.
          const product = xCoverProducts.find(product => product.protectionType === 'full') ?? xCoverProducts[0]

          dispatch({
            api: OPT_OUT_OF_QUOTE,
            type: API_CALL,
            request: () => sendOptOutRequest(quoteId, [product.id]),
          })
        }
      }
    }

    // PassportCard opt-out: send only when the customer explicitly selected PassportCard no protection,
    // no paid PassportCard item is selected, and the matching quote/proposal exists.
    const passportCardOptOutData = getPassportCardOptOutDataFromCheckout(state)
    const hasPassportCardNoProtectionItem = !!noProtectionPassportCardInsuranceItem
    const hasPurchasedPassportCardInsuranceItem = !!purchasedPassportCardInsuranceItem
    const hasPassportCardOptOutQuoteAndProposal = !!passportCardOptOutData
    const shouldSendPassportCardOptOut = (
      hasPassportCardNoProtectionItem &&
      !hasPurchasedPassportCardInsuranceItem &&
      hasPassportCardOptOutQuoteAndProposal
    )

    if (shouldSendPassportCardOptOut && passportCardOptOutData) {
      dispatch({
        api: OPT_OUT_OF_QUOTE,
        type: API_CALL,
        request: () => sendPassportCardOptOutRequest(
          passportCardOptOutData.quoteId,
          passportCardOptOutData.proposalId,
        ),
      })
    }
  }
}

export function fetchInsuranceProducts(
  type: App.InsuranceProductType,
  params: {
    isDomestic?: boolean
    isCruise?: boolean
    subscriptionId?: string
  } = {},
): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()

    const key = getInsuranceProductListKey(type, params)

    if (state.insurance.productLists[key] ||
      state.insurance.fetchingProductLists[key] ||
      state.insurance.productListErrors[key]
    ) {
      // already attempted it
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_INSURANCE_PRODUCTS,
      key,
      request: () => {
        if (params.subscriptionId) {
          return getInsuranceProductsBySubscription(params.subscriptionId, state.geo.currentRegionCode)
        } else {
          return getInsuranceProducts(
            state.geo.currentRegionCode,
            type,
            {
              isDomestic: params.isDomestic,
              isCruise: params.isCruise,
            })
        }
      },
    })
  }
}

export function resetInsurance() {
  return {
    type: RESET_INSURANCE,
  }
}

/**
 * Invalidates all cached insurance quotes.
 * This should be called when the traveller count changes to force a refetch
 * with the updated traveller count, preventing quote/order mismatches.
 *
 * @param reason Optional reason for invalidation (for debugging)
 */
export function invalidateInsuranceQuotes(reason?: string) {
  return {
    type: INVALIDATE_INSURANCE_QUOTES,
    reason,
  }
}

interface UpdateQuoteParams {
  startDate: string
  endDate: string
  travellers: Array<App.OrderInsuranceItemTraveller>
  insuranceItem: App.OrderInsuranceItem
  coverageAmount?: number
}

export function fetchOrderInsuranceUpdateQuote(
  orderId: string,
  update: UpdateQuoteParams,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const key = getOrderInsuranceUpdateQuoteKey({
      endDate: update.endDate,
      startDate: update.startDate,
      itemId: update.insuranceItem.id,
      travellers: update.travellers,
      coverageAmount: update.coverageAmount ?? update.insuranceItem.coverAmount,
    })

    if (state.insurance.updateQuotes[key]) {
      // already tried to fetch it
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_INSURANCE_UPDATE_QUOTE,
      request: () => getInsuranceUpdateQuote(orderId, {
        coverageAmount: update.coverageAmount ?? update.insuranceItem.coverAmount,
        endDate: update.endDate,
        startDate: update.startDate,
        subscriptionId: update.insuranceItem.subscriptionId,
        travellers: update.travellers,
      }),
      key,
    })
  }
}

interface InsuranceUpdateParams {
  updateId: string
  coverAmount: number
  transactionKey: string
  total: number
  operation: 'change_dates' | 'change_guest_details'
  paymentPlan?: App.PaymentPlan
}

export function updateOrderInsurance(
  orderId: string,
  requestDetails: InsuranceUpdateParams,
): AppAction {
  return (dispatch) => {
    dispatch({
      type: API_CALL,
      api: UPDATE_INSURANCE,
      request: async() => {
        await updateInsurance(orderId, requestDetails)
        const updatedOrder = await getFullOrder(orderId)
        dispatch(updateOrder(updatedOrder))
        dispatch(pushWithRegion(`/account/my-escapes/order/${orderId}`))
      },
      orderId,
    })
  }
}

export function fetchPassportCardCustomerDetails(subscriptionId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (
      state.insurance.passportCardCustomerDetails[subscriptionId] ||
      state.insurance.fetchingPassportCardCustomerDetails[subscriptionId]
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_PASSPORT_CARD_CUSTOMER_DETAILS,
      subscriptionId,
      request: () => getPassportCardCustomerDetails(subscriptionId),
    })
  }
}

export function updatePassportCardInsuranceCustomerDetails(
  orderId: string,
  subscriptionId: string,
  newDetails: { firstName: string; lastName: string; email?: string; phone?: string; dateOfBirth?: string },
): AppAction {
  return (dispatch) => {
    dispatch({
      type: API_CALL,
      api: UPDATE_INSURANCE,
      request: async() => {
        await updatePassportCardCustomerDetailsApi(subscriptionId, newDetails)
        dispatch(pushWithRegion(`/account/my-escapes/order/${orderId}`))
      },
      orderId,
    })
  }
}
